import {
  ImpactCategoryOption,
  IndicatorOption,
  ReportFormatOption,
  ReportFormatUiRules,
  ReportGenerationDefaults,
  ReportGenerationFormData,
  ReportTypeOption,
  VersionOption,
} from './report-generation-form.model';

export const EMPTY_UI_RULES: ReportFormatUiRules = {
  showReportTitle: false,
  showReportType: false,
  showImpactCategory: false,
  showCategoriesToInclude: false,
  showIncludeInputInformation: false,
  showFunctionalUnit: false,
  showBaseScenario: false,
  showFunctionalUnitText: false,
  showSensitivityAnalysis: false,
  showVersion: false,
  maxScenarios: null,
  maxCategories: null,
  scenarioHint: '',
  categoryHint: '',
};

const EMPTY_DEFAULTS: ReportGenerationDefaults = {
  reportFormatId: null,
  reportTypeId: null,
  legacyReportFormatId: null,
  isCustomType: false,
  reportTitle: '',
  scenarioIds: [],
  baseScenarioId: null,
  functionalUnitId: null,
  impactCategoryId: null,
  indicatorIds: [],
  additionalElementIds: [],
  versionId: null,
  isIncludeInputInformation: false,
  isExcludeProductImpacts: false,
  sensitivityAnalyses: [],
};

export function toReportGenerationFormData(response: any): ReportGenerationFormData {
  const data = (response?.data ?? {}) as Partial<ReportGenerationFormData>;

  return {
    project: data.project ?? { id: 0, name: '', functionalUnitText: '' },
    organization: data.organization ?? { id: 0, name: '' },
    reportFormats: (data.reportFormats ?? []).map(normalizeFormat),
    scenarios: data.scenarios ?? [],
    functionalUnits: data.functionalUnits ?? [],
    impactCategories: data.impactCategories ?? [],
    indicators: data.indicators ?? [],
    additionalCategories: data.additionalCategories ?? [],
    versions: data.versions ?? [],
    defaults: { ...EMPTY_DEFAULTS, ...(data.defaults ?? {}) },
    invalidEolScenarios: data.invalidEolScenarios ?? [],
    incorrectFillingScenarios: data.incorrectFillingScenarios ?? [],
  };
}

function normalizeFormat(format: ReportFormatOption): ReportFormatOption {
  return {
    ...format,
    ui: { ...EMPTY_UI_RULES, ...(format?.ui ?? {}) },
    reportTypeCategories: (format?.reportTypeCategories ?? []).map(category => ({
      ...category,
      types: (category?.types ?? []).map(type => ({
        ...type,
        indicatorIds: type?.indicatorIds ?? [],
      })),
    })),
  };
}

export function findFormat(
  data: ReportGenerationFormData,
  formatId: number | null | undefined
): ReportFormatOption | null {
  if (formatId === null || formatId === undefined) return null;
  return data.reportFormats.find(x => x.id === Number(formatId)) ?? null;
}

export function allTypes(format: ReportFormatOption | null): ReportTypeOption[] {
  if (!format) return [];
  return format.reportTypeCategories.reduce(
    (acc: ReportTypeOption[], category) => acc.concat(category.types ?? []),
    []
  );
}

export function findType(
  format: ReportFormatOption | null,
  typeId: number | null | undefined,
  isCustom: boolean,
  legacyReportFormatId?: number | null
): ReportTypeOption | null {
  if (!format || typeId === null || typeId === undefined) return null;
  const candidates = allTypes(format).filter(x => x.id === Number(typeId));
  if (!candidates.length) return null;
  if (legacyReportFormatId !== null && legacyReportFormatId !== undefined) {
    const byLegacyId = candidates.find(
      x => Number(x.legacyReportFormatId) === Number(legacyReportFormatId)
    );
    if (byLegacyId) return byLegacyId;
  }
  return candidates.find(x => x.isCustom === isCustom) ?? candidates[0];
}

export function findImpactCategory(
  data: ReportGenerationFormData,
  impactCategoryId: number | null | undefined
): ImpactCategoryOption | null {
  if (impactCategoryId === null || impactCategoryId === undefined) return null;
  return data.impactCategories.find(x => x.id === Number(impactCategoryId)) ?? null;
}

export function resolveIndicators(
  data: ReportGenerationFormData,
  type: ReportTypeOption | null
): IndicatorOption[] {
  if (type?.indicatorIds?.length) {
    const allowed = new Set(type.indicatorIds.map(Number));
    return data.indicators.filter(x => allowed.has(Number(x.id)));
  }
  return data.indicators;
}

export function resolveLegacyReportFormatId(
  format: ReportFormatOption | null,
  type: ReportTypeOption | null
): number {
  if (type) return Number(type.legacyReportFormatId);
  return Number(format?.legacyReportFormatId ?? 0);
}

export function showVersionFor(
  format: ReportFormatOption | null,
  type: ReportTypeOption | null,
  versions: VersionOption[]
): boolean {
  if (!versions?.length) return false;
  return Boolean(format?.ui?.showVersion) || Boolean(type?.showVersion);
}

export function defaultVersionId(versions: VersionOption[]): number | null {
  const selected = versions.find(x => x.selected);
  return selected ? selected.id : versions.length ? versions[0].id : null;
}

export function visibleCategories(format: ReportFormatOption | null) {
  if (!format) return [];
  return format.reportTypeCategories.filter(
    category => category.id !== 3 || (category.types?.length ?? 0) > 0
  );
}
